import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { useApp, API } from "../store";
import { HeroVideo } from "../homeData";
import { Feather, Battery, Zap, Wifi, Plug, Award, Globe, Wrench } from "lucide-react";
import { TrustModules } from "../components/TrustModules";

const FEATURE_ICONS = [Feather, Battery, Zap, Wifi];

// Lifestyle imagery per category
const LIFESTYLE = {
  efoils: [
    "/api/uploads/8422f2cc0d1149d19f22a8a09a1fd5c1.webp",
    "/api/uploads/809533a2a15e40a4accda86cf267c1ab.webp",
    "/api/uploads/239dfcb3d52e4753bfdf8e71891c7b6b.webp",
    "/api/uploads/87f0b66fcbc4452fb9f1852d8bfef551.webp",
  ],
  jetboards: [
    "https://images.unsplash.com/photo-1667478828745-0b3ec0c3f97a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "https://images.unsplash.com/photo-1769816325482-59769ff9ab37?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "https://images.unsplash.com/photo-1763075958748-9a0c9c8799c3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  ],
  accessories: [
    "https://images.unsplash.com/photo-1763075958748-9a0c9c8799c3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  ],
};

export default function ProductList() {
  const { category } = useParams();
  const { t, formatPrice, country, locale } = useApp();
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products?category=${category}`).then((r) => setItems(r.data));
  }, [category]);

  const cat = t.catPages?.[category];

  const features = cat ? [
    { t: cat.f1t, d: cat.f1d },
    { t: cat.f2t, d: cat.f2d },
    { t: cat.f3t, d: cat.f3d },
    { t: cat.f4t, d: cat.f4d },
  ] : [];

  const scrollToProducts = (e) => {
    e.preventDefault();
    const el = document.getElementById("products-grid");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const ctaLabel = (() => {
    if (category === "efoils") return locale === "pt" ? "Customize seu E-Foil" : locale === "es" ? "Customize su E-Foil" : "Customize your E-Foil";
    if (category === "jetboards") return locale === "pt" ? "Escolher sua JetBoard" : locale === "es" ? "Elegir tu JetBoard" : "Choose your JetBoard";
    return cat?.cta || (locale === "pt" ? "Escolher opções" : locale === "es" ? "Elegir opciones" : "Choose options");
  })();

  const richSections = RICH_CONTENT[category]?.[locale] || RICH_CONTENT[category]?.en || [];
  const lifestyleImgs = LIFESTYLE[category] || [];

  return (
    <main className="bg-white" data-testid={`list-${category}`}>
      {/* Hero with video */}
      <section className="relative h-[80vh] overflow-hidden mt-16">
        <HeroVideo overlay="linear-gradient(180deg, rgba(15,23,42,0.30) 0%, rgba(15,23,42,0.10) 50%, rgba(255,255,255,0.0) 100%)" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-white/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-[1440px] mx-auto">
            <div className="overline mb-3">JETBOARDS</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl md:text-8xl text-slate-900">{cat?.heroTitle || category}</h1>
            <p className="text-slate-700 mt-3 text-base md:text-xl max-w-2xl italic font-serif-display">{cat?.heroSub}</p>
            {cat && (
              <a href="#products-grid" onClick={scrollToProducts} className="btn-primary mt-8 inline-flex" data-testid={`cat-cta-top-${category}`}>
                {ctaLabel} →
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Feature blocks */}
      {features.length > 0 && (
        <section className="py-16 md:py-24 px-6 md:px-12 max-w-[1440px] mx-auto" data-testid={`cat-features-${category}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((f, i) => {
              const Icon = FEATURE_ICONS[i] || Zap;
              return (
                <div key={i} className="card-clean p-7 md:p-10 flex gap-5">
                  <div className="w-12 h-12 shrink-0 rounded-full bg-sky-50 grid place-items-center text-sky-500">
                    <Icon size={20} />
                  </div>
                  <div>
                    <div className="font-serif-display text-2xl md:text-3xl text-slate-900">{f.t}</div>
                    <p className="text-slate-600 mt-2 leading-relaxed text-sm md:text-base">{f.d}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Rich content split sections (alternating image + text) */}
      {richSections.map((sec, i) => (
        <section key={i} className={`py-16 md:py-24 px-6 md:px-12 ${i % 2 === 0 ? "bg-slate-50" : "bg-white"}`}>
          <div className="max-w-[1440px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div className={i % 2 === 1 ? "md:order-2" : ""}>
              <img src={sec.img || lifestyleImgs[i % lifestyleImgs.length] || lifestyleImgs[0]} alt={sec.t}
                className="w-full aspect-[5/4] object-cover rounded-md shadow-xl" />
            </div>
            <div className={i % 2 === 1 ? "md:order-1" : ""}>
              <div className="overline mb-3">{sec.kicker}</div>
              <h3 className="font-serif-display text-3xl md:text-5xl text-slate-900 leading-tight">{sec.t}</h3>
              <p className="text-slate-700 mt-5 leading-relaxed whitespace-pre-line text-sm md:text-base">{sec.d}</p>
              {sec.bullets && (
                <ul className="mt-5 space-y-2">
                  {sec.bullets.map((b, k) => (
                    <li key={k} className="flex items-start gap-2 text-slate-700 text-sm md:text-base">
                      <span className="text-sky-500 mt-1">●</span> {b}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      ))}

      {/* Stats strip */}
      {(category === "efoils" || category === "jetboards") && (
        <section className="py-16 px-6 md:px-12 bg-gradient-to-br from-sky-500 to-cyan-500 text-white">
          <div className="max-w-[1440px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <Stat n={category === "efoils" ? "19 kg" : "23 kg"} l={locale === "pt" ? "Peso total mínimo" : locale === "es" ? "Peso total mínimo" : "Min Total weight"} />
            <Stat n="45 km/h" l={locale === "pt" ? "Velocidade máxima" : locale === "es" ? "Velocidad máxima" : "Top speed"} />
            <Stat n="3 s" l={locale === "pt" ? "Para ir pra água" : locale === "es" ? "Listo para el agua" : "Ready for water"} />
            <Stat n="100%" l={locale === "pt" ? "Elétrico" : locale === "es" ? "Eléctrico" : "Electric"} />
          </div>
        </section>
      )}

      {/* Product grid */}
      <section id="products-grid" className="px-6 md:px-12 max-w-[1440px] mx-auto pt-16 pb-16 scroll-mt-20">
        <div className="text-center mb-10">
          <div className="overline mb-2">{t.productList.buildYours}</div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-slate-900">{ctaLabel}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((p) => (
            <Link to={`/product/${p.slug}`} key={p.id} className="group card-clean overflow-hidden block" data-testid={`item-${p.slug}`}>
              <div className="aspect-[4/5] overflow-hidden bg-slate-50 relative">
                <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                {country === "BR" && (
                  <div className="absolute top-3 left-3 spec-pill bg-emerald-500/95 text-white border-0 backdrop-blur-sm" data-testid={`br-badge-${p.slug}`}>
                    🇧🇷 {t.product.brDelivery}
                  </div>
                )}
              </div>
              <div className="p-6 md:p-7">
                <div className="font-serif-display text-2xl md:text-3xl group-hover:text-sky-500 transition-colors text-slate-900">{p.name}</div>
                <div className="text-slate-600 text-sm mt-2 line-clamp-2">{p.tagline}</div>
                <div className="flex items-end justify-between mt-5 gap-2">
                  <div>
                    <div className="overline">{t.product.from}</div>
                    <div className="font-serif-display text-2xl md:text-3xl text-sky-600">{formatPrice(p)}</div>
                  </div>
                  <div className="btn-primary mt-auto shrink-0">{t.product.chooseOptions} →</div>
                </div>
              </div>
            </Link>
          ))}
          {items.length === 0 && <div className="text-slate-500 col-span-full">{t.productList.noItems}</div>}
        </div>
      </section>
      <TrustModules category={category} locale={locale} />
    </main>
  );
}

function Stat({ n, l }) {
  return (
    <div data-testid="cat-stat">
      <div className="font-serif-display text-4xl md:text-6xl">{n}</div>
      <div className="text-xs md:text-sm uppercase tracking-widest mt-1 opacity-90">{l}</div>
    </div>
  );
}

// ---------- Rich content (per category, per locale) ----------
const RICH_CONTENT = {
  efoils: {
    en: [
      {
        kicker: "One of the lightest e-foils",
        t: "From 19 kg of pure carbon performance.",
        d: "Produced in Brazil from imported carbon fiber and high-performance components, the SharkX Ultra Light E-Foil starts at 19 kg. Weight varies by battery selection. Lighter is better. Period.",
        bullets: ["High-density battery cells", "All high-quality imported components", "Designed for performance and easy learning"],
      },
      {
        kicker: "World distance record",
        t: "Up to 52 km on a single charge",
        d: "Our Extreme Range battery holds the official world record for the longest distance covered on a single battery charge — record achieved by a 94 kg rider. Lighter riders can go even further. Choose from 2 to 6 hours of fun on the water depending on the pack.",
        bullets: ["Modular battery — same pack works in e-foils and jetboards", "Fast-charge SharkX charger included", "IP68 wireless waterproof remote"],
      },
      {
        kicker: "Plug & Surf",
        t: "Ready for the water in 6 seconds",
        d: "Our patented quick plug & surf system allows battery swaps in 3 seconds — no caps or cables. Quick mast engagement in 3 seconds. No time wasted with screws or tools",
        bullets: ["Snap-in carbon mast without screws", "Fully waterproof battery with patented cooling system", "No time wasted screwing the mast to the board or searching for tools or lost screws"],
      },
      {
        kicker: "Wings & Setups",
        t: "One set, infinite setups.",
        d: "Modular ecosystem — invest once. Board, mast, and battery models are interchangeable. Start with an easier learning setup and as you progress, switch to a smaller board with a performance wing or a larger/smaller battery. Choose between SharkX Easy wings (stable, beginner-friendly, longest battery life) or SharkX Performance wings (tighter turns, higher top speeds). Swap wings in 3 minutes and have a totally different ride. The same propulsion + battery powers all models. Swap as you wish!",
        bullets: ["SharkX CRUISE E-Foil — If it's your first e-foil", "Easy 1800 wing recommended for starting in the sport. Easy to learn with good maneuverability", "Mast 73cm standard with carbon fuselage"],
      },
    ],
    pt: [
      {
        kicker: "Um dos e-foils mais leves",
        t: "A partir de 19 kg de pura performance em fibra de carbono.",
        d: "Produzido no Brasil em fibra de carbono importada com componentes de alta performance, o SharkX Ultra Light E-Foil começa em 19 kg. O peso varia de acordo com a bateria selecionada. Quanto mais leve, melhor. Ponto.",
        bullets: ["Células de bateria de alta densidade", "Todos componentes importados de altíssima qualidade", "Projetado para performance e fácil aprendizado"],
      },
      {
        kicker: "Recorde mundial de distância",
        t: "Até 52 km em uma única carga",
        d: "Nossa bateria Extreme Range detém o recorde mundial oficial de maior distância percorrida em uma única carga de bateria — Recorde atingido por um rider de 94 kg. Riders mais leves poderão ir ainda mais longe. Escolha de 2 horas a 6 horas de diversão na água conforme o pack.",
        bullets: ["Bateria modular — mesmo pack serve para e-foils e jetboards", "Carregador Fast-charge SharkX incluso", "Controle wireless IP68 à prova d'água"],
      },
      {
        kicker: "Plug & Surf",
        t: "Pronto para ir para a água em 6 segundos",
        d: "Nosso sistema patenteado quick plug & surf permite trocar baterias em 3 segundos — sem tampas ou cabos. Engate rápido do mastro em 3 segundos. Sem perder tempo com parafusos ou ferramentas",
        bullets: ["Mastro com engate rápido de carbono sem parafusos", "Bateria totalmente a prova d'água com sistema de resfriamento patenteado", "Sem tempo perdido aparafusando mastro na prancha ou procurando ferramentas ou parafusos perdidos"],
      },
      {
        kicker: "Asas & Setups",
        t: "Um conjunto, infinitos setups.",
        d: "Ecossistema modular — invista uma vez. Os modelos de pranchas, mastros e baterias são intercambiáveis. Comece com um setup mais fácil de aprender e a medida que for progredindo troque por uma prancha menor com uma asa mais performance ou uma bateria maior ou menor. Escolha entre asas SharkX Easy (estáveis, fáceis para iniciantes, maior duração de bateria) ou asas SharkX Performance (curvas fechadas, altas velocidades). Troque a asa em 3 minutos e tenha um equipamento totalmente diferente. A mesma propulsão + bateria alimenta todos os modelos. Troque como quiser!",
        bullets: ["SharkX CRUISE E-Foil — Se for seu primeiro e-foil", "Asa Easy 1800 recomendada para iniciar no esporte. Fácil de aprender com boa manobrabilidade", "Mastro 73cm padrão com fuselagem de carbono"],
      },
    ],
    es: [
      {
        kicker: "El e-foil más liviano del mundo",
        t: "Desde 19 kg de pura performance en fibra de carbono.",
        d: "Fabricado en Brasil con fibra de carbono importada y componentes de alta performance, el SharkX Ultra Light E-Foil comienza en 19 kg. El peso varía según la batería seleccionada. Más liviano es mejor. Punto.",
        bullets: ["Células de batería de alta densidad", "Todos los componentes importados de alta calidad", "Diseñado para performance y aprendizaje fácil"],
      },
      {
        kicker: "Récord mundial de distancia",
        t: "Hasta 52 km con una sola carga",
        d: "Nuestra batería Extreme Range posee el récord mundial oficial de mayor distancia recorrida en una sola carga de batería — récord logrado por un rider de 94 kg. Riders más livianos podrán ir aún más lejos. Elegí de 2 a 6 horas de diversión en el agua según el pack.",
        bullets: ["Batería modular — el mismo pack para e-foils y jetboards", "Cargador Fast-charge SharkX incluido", "Control wireless IP68 acuático"],
      },
      {
        kicker: "Plug & Surf",
        t: "Listo para el agua en 6 segundos",
        d: "Nuestro sistema patentado quick plug & surf permite cambiar baterías en 3 segundos — sin tapas ni cables. Enganche rápido del mástil en 3 segundos. Sin perder tiempo con tornillos o herramientas",
        bullets: ["Mástil de carbono con enganche rápido sin tornillos", "Batería totalmente impermeable con sistema de enfriamiento patentado", "Sin tiempo perdido atornillando el mástil a la tabla o buscando herramientas o tornillos perdidos"],
      },
      {
        kicker: "Alas & Setups",
        t: "Un conjunto, infinitos setups.",
        d: "Ecosistema modular — invertís una sola vez. Los modelos de tablas, mástiles y baterías son intercambiables. Empezá con un setup más fácil de aprender y a medida que progreses cambiá por una tabla más chica con un ala más performance o una batería mayor o menor. Elegí entre alas SharkX Easy (estables, fáciles para principiantes, máxima duración de batería) o SharkX Performance (curvas cerradas, alta velocidad). Cambia el ala en 3 minutos y tendrás un equipo totalmente diferente. La misma propulsão + batería alimenta todos los modelos. ¡Cambiá como quieras!",
        bullets: ["SharkX CRUISE E-Foil — Si es tu primer e-foil", "Ala Easy 1800 recomendada para iniciar en el deporte. Fácil de aprender con buena maniobrabilidad", "Mástil 73cm estándar con fuselaje de carbono"],
      },
    ],
  },
  jetboards: {
    en: [
      {
        kicker: "The lighter, the better. Period.",
        t: "The lighter, the better. Period.",
        d: "Nearly half the weight of competitors with the same battery life! (Around 20kg lighter) The SharkX Jetboard is built in Brazil from imported carbon fiber and high-performance imported components. With the Ultra-Light battery, total weight with battery starts at 23 kg. (Weight varies by selected battery)",
        bullets: ["Built in carbon fiber. Lightness and strength.", "Fully waterproof battery with patented cooling system", "Designed for maximum lightness and performance"],
        img: "/api/uploads/1543af15cbc74ad28c298334da864841.webp",
      },
      {
        kicker: "Modular ecosystem",
        t: "Modular ecosystem",
        d: "The most expensive items on a jetboard are the battery and the turbine. That's why we created a modular system where you can use the same battery and same turbine on any of our jetboard models. — Use different models of our jetboards using the same battery and turbine. Purchase only the extra board and you'll have a totally different setup at the lowest possible price. Other brands don't offer this option.",
        bullets: ["Swap boards using the same turbine and battery in 5 minutes", "One propulsion system, different possible combinations", "Future-fins quad-fin system included"],
        img: "/api/uploads/7cc7f14ea94040718e9e40402280dd9e.webp",
      },
      {
        kicker: "Three styles, your call",
        t: "Freeride, Waves Pro or SUP.",
        d: "- SharkX Freeride 6'4″: wider, more stable (76.2 cm / 103 L). Ideal for starting in the sport due to its ease of riding and for sharing with friends and family.\n- SharkX Waves Pro 5'9″ or 6'4″: narrower (60–64 cm), more agile and aggressive. Perfect for tight turns and surfing waves without paddling. The 5'9″ fits riders up to 75 kg; the 6'4″ up to 100 kg.\n- SharkX SUP 10'0\" or 11'0\": carries up to 2 people. Recommended for longer rides or family outings, fishing, and even rescue. It's like an electric kayak or jet ski, but with the mobility of being able to carry it on your car rack!",
        bullets: ["Freeride: stability + easy to ride", "Waves Pro: tight turns + wave riding", "SUP: carries up to 2 people"],
        img: "/api/uploads/9f7f72d899cb4f3bac2ecc7158c5ccb4.webp",
      },
      {
        kicker: "Endless rides",
        t: "One set, infinite setups.",
        d: "Our fully waterproof battery with patented quick plug & surf system lets you swap batteries in 3 seconds. No cables to connect or caps. — The same battery pack works across the entire SharkX line. Choose your battery option for up to 1 hour of fun on the water.",
        bullets: ["IP68 wireless waterproof trigger remote", "Fast-charge SharkX charger included", "Modular pack — also fits the e-foil"],
        img: "/api/uploads/5e21b9e2c78e4b2e8b324926f95f68a2.webp",
      },
    ],
    pt: [
      {
        kicker: "Quanto mais leve melhor. Ponto.",
        t: "Quanto mais leve melhor. Ponto.",
        d: "Quase a metade do peso de concorrentes com a mesma duração de bateria! (Em torno de 20kg mais leve) A SharkX Jetboard é construída no Brasil em fibra de carbono importada, com componentes importados de alta performance. Com a bateria Ultra-Light, o peso total total com bateria começa em 23 kg. (O peso varia conforme a bateria selecionada)",
        bullets: ["Construída em fibra de carbono. Leveza e resistência.", "Bateria totalmente a prova d'água com sistema de resfriamento patenteado", "Projetado para máxima leveza e performance"],
        img: "/api/uploads/1543af15cbc74ad28c298334da864841.webp",
      },
      {
        kicker: "Ecossistema modular",
        t: "Ecossistema modular",
        d: "Os itens mais caros de uma jetboard são a bateria e a turbina. Por isso criamos um sistema modular onde você pode usar a mesma bateria e mesma turbina em qualquer modelo das nossas jetboards. — Use diferentes modelos das nossas jetboards usando a mesma bateria e turbina. Adquira somente a prancha extra e você terá outro setup totalmente diferente com o menor preço possível. Outras marcas não oferecem esta opção.",
        bullets: ["Troque de prancha usando a mesma turbina e bateria em 5 minutos", "Um sistema de propulsão, diferentes combinações possíveis", "Sistema Future-fins quad incluso"],
        img: "/api/uploads/7cc7f14ea94040718e9e40402280dd9e.webp",
      },
      {
        kicker: "Três estilos, sua escolha",
        t: "Freeride, Waves Pro ou SUP.",
        d: "- SharkX Freeride 6'4″: mais larga e estável (76,2 cm / 103 L). Ideal para iniciar no esporte pela facilidade de andar e para compartilhar com amigos e familiares.\n- SharkX Waves Pro 5'9″ ou 6'4″: mais estreita (60–64 cm), mais ágil e agressiva. Perfeita para curvas fechadas e surfar ondas sem precisar remar. A 5'9″ é recomendada para riders até 75 kg; a 6'4″ até 100 kg.\n- SharkX SUP 10'0\" ou 11'0\": leva até 2 pessoas. Recomendada para passeios mais longos ou em família, para pescar e até para salvamento. É como um caiaque elétrico ou jet ski, porém com a mobilidade de poder levar ele no rack do seu carro!",
        bullets: ["Freeride: estabilidade + fácil de andar", "Waves Pro: curvas fechadas + ondas", "SUP: leva até 2 pessoas"],
        img: "/api/uploads/9f7f72d899cb4f3bac2ecc7158c5ccb4.webp",
      },
      {
        kicker: "Rides sem fim",
        t: "Um conjunto, infinitos setups.",
        d: "Nossa bateria totalmente a prova d'água com sistema patenteado quick plug & surf permite trocar baterias em 3 segundos. Sem cabos para conectar ou tampas. — O mesmo pack de bateria serve para toda a linha SharkX. Escolha sua opção de bateria para até 1 hora de diversão na água.",
        bullets: ["Controle wireless IP68 à prova d'água", "Carregador Fast-charge SharkX incluso", "Pack modular — também serve no e-foil"],
        img: "/api/uploads/5e21b9e2c78e4b2e8b324926f95f68a2.webp",
      },
    ],
    es: [
      {
        kicker: "Cuanto más liviana, mejor. Punto.",
        t: "Cuanto más liviana, mejor. Punto.",
        d: "¡Casi la mitad del peso de competidores con la misma duración de batería! (Alrededor de 20 kg más liviana) La SharkX Jetboard se fabrica en Brasil en fibra de carbono importada, con componentes importados de alta performance. Con la batería Ultra-Light, el peso total con batería comienza en 23 kg. (El peso varía según la batería seleccionada)",
        bullets: ["Construida en fibra de carbono. Ligereza y resistencia.", "Battería totalmente a prueba de agua con sistema de refrigeración patentado", "Diseñado para máxima ligereza y performance"],
        img: "/api/uploads/1543af15cbc74ad28c298334da864841.webp",
      },
      {
        kicker: "Ecosistema modular",
        t: "Ecosistema modular",
        d: "Los artículos más caros de una jetboard son la batería y la turbina. Por eso creamos un sistema modular donde podés usar la misma batería y misma turbina en cualquiera de nuestros modelos de jetboards. — Usá diferentes modelos de nuestras jetboards usando la misma batería y turbina. Adquirí solamente la tabla extra y tendrás otro setup totalmente diferente con el menor precio posible. Otras marcas no ofrecen esta opción.",
        bullets: ["Cambiá de tabla usando la misma turbina y batería en 5 minutos", "Un sistema de propulsión, diferentes combinaciones posibles", "Sistema Future-fins quad incluido"],
        img: "/api/uploads/7cc7f14ea94040718e9e40402280dd9e.webp",
      },
      {
        kicker: "Tres estilos, tu elección",
        t: "Freeride, Waves Pro o SUP.",
        d: "- SharkX Freeride 6'4″: más ancha y estable (76,2 cm / 103 L). Ideal para iniciar en el deporte por la facilidad de andar y para compartir con amigos y familia.\n- SharkX Waves Pro 5'9″ o 6'4″: más angosta (60–64 cm), más ágil y agresiva. Perfecta para curvas cerradas y surfear olas sin remar. La 5'9″ es recomendada para riders de hasta 75 kg; la 6'4″ hasta 100 kg.\n- SharkX SUP 10'0\" o 11'0\": lleva hasta 2 personas. Recomendada para paseos más largos o en familia, para pescar e incluso para rescate. ¡Es como un kayak eléctrico o jet ski, pero con la movilidad de poder llevarlo en el rack de tu auto!",
        bullets: ["Freeride: estabilidad + fácil de andar", "Waves Pro: curvas cerradas + olas", "SUP: lleva hasta 2 personas"],
        img: "/api/uploads/9f7f72d899cb4f3bac2ecc7158c5ccb4.webp",
      },
      {
        kicker: "Rides sin fin",
        t: "Un conjunto, infinitos setups.",
        d: "Nuestra batería totalmente a prueba de agua con sistema patentado quick plug & surf permite cambiar baterías en 3 segundos. Sin cables para conectar ni tapas. — El mismo pack de batería funciona en toda la línea SharkX. Elegí tu opción de batería para hasta 1 hora de diversión en el agua.",
        bullets: ["Control wireless IP68 acuático", "Cargador Fast-charge SharkX incluido", "Pack modular — también sirve en e-foil"],
        img: "/api/uploads/5e21b9e2c78e4b2e8b324926f95f68a2.webp",
      },
    ],
  },
};
